import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./RepairSlider.module.scss";

interface RepairSliderProps {
  images: string[];
  alt: string;
  maxHeight?: number;
}

const SLIDE_RATIO = 0.54;        // two-column desktop (>1200px)
const SLIDE_RATIO_COLUMN = 0.7;  // column layout (481–1200px)
const GAP = 12;
const AUTOPLAY_DELAY = 5000;
const SWIPE_THRESHOLD = 40;

const RepairSlider: React.FC<RepairSliderProps> = ({ images, alt, maxHeight }) => {
  const looped = [images[images.length - 1], ...images, images[0]];

  const [index, setIndex] = useState(1);
  const [animated, setAnimated] = useState(true);
  const [width, setWidth] = useState(0);
  const [imgRatio, setImgRatio] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 480 : false
  );
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth > 956 : true
  );
  const viewportRef = useRef<HTMLDivElement>(null);
  const lockRef = useRef(false);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pointerStartX = useRef<number | null>(null);

  // Track viewport element width
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Track window width for layout detection
  useEffect(() => {
    const handler = () => {
      setIsMobile(window.innerWidth <= 480);
      setIsDesktop(window.innerWidth > 956);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Detect image aspect ratio from first loaded image
  const handleImgLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    if (imgRatio !== null) return;
    const img = e.currentTarget;
    if (img.naturalWidth > 0) {
      setImgRatio(img.naturalHeight / img.naturalWidth);
    }
  }, [imgRatio]);

  // Slide ratio: bigger in column mode so peek areas are smaller
  const slideRatio = isDesktop ? SLIDE_RATIO : SLIDE_RATIO_COLUMN;
  const slideWidth = width * slideRatio;
  const sideOffset = (width - slideWidth) / 2;
  const trackTransform = isMobile
    ? `translateX(-${index * width}px)`
    : `translateX(${sideOffset - index * (slideWidth + GAP)}px)`;

  // Height = slideWidth × image aspect ratio → exact fit for cover (no cropping)
  // Falls back to maxHeight until first image loads
  const naturalHeight = slideWidth > 0 && imgRatio !== null ? slideWidth * imgRatio : null;
  const viewportHeight = !isMobile
    ? (naturalHeight !== null ? naturalHeight : maxHeight)
    : undefined;

  const next = useCallback(() => {
    if (lockRef.current) return;
    lockRef.current = true;
    setIndex((i) => i + 1);
  }, []);

  const prev = useCallback(() => {
    if (lockRef.current) return;
    lockRef.current = true;
    setIndex((i) => i - 1);
  }, []);

  const goTo = useCallback((realIdx: number) => {
    if (lockRef.current) return;
    lockRef.current = true;
    setIndex(realIdx + 1);
  }, []);

  // Autoplay
  const startAutoplay = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    autoplayRef.current = setInterval(() => {
      if (!lockRef.current) {
        lockRef.current = true;
        setIndex((i) => i + 1);
      }
    }, AUTOPLAY_DELAY);
  }, []);

  useEffect(() => {
    startAutoplay();
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [startAutoplay]);

  // Pause autoplay when tab is hidden, resume when visible
  // Prevents index jumping out of bounds after browser throttles intervals
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        if (autoplayRef.current) clearInterval(autoplayRef.current);
      } else {
        startAutoplay();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [startAutoplay]);

  // After transition — unlock; on clone snap silently to real slide
  const handleTransitionEnd = useCallback(() => {
    if (index === 0) {
      setAnimated(false);
      setIndex(images.length);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimated(true);
          lockRef.current = false;
        });
      });
    } else if (index === looped.length - 1) {
      setAnimated(false);
      setIndex(1);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimated(true);
          lockRef.current = false;
        });
      });
    } else {
      lockRef.current = false;
    }
  }, [index, images.length, looped.length]);

  // Swipe / drag (works on both touch and desktop)
  const handlePointerDown = (e: React.PointerEvent) => {
    pointerStartX.current = e.clientX;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (pointerStartX.current === null) return;
    const delta = e.clientX - pointerStartX.current;
    pointerStartX.current = null;
    if (Math.abs(delta) > SWIPE_THRESHOLD) {
      if (delta < 0) { next(); startAutoplay(); }
      else { prev(); startAutoplay(); }
    }
  };

  const handlePointerCancel = () => {
    pointerStartX.current = null;
  };

  const realIndex = (((index - 1) % images.length) + images.length) % images.length;


  return (
    <div className={styles.root}>
      <div
        className={styles.viewport}
        ref={viewportRef}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        style={{
          touchAction: "pan-y",
          cursor: "grab",
          userSelect: "none",
          ...(viewportHeight !== undefined ? { height: viewportHeight } : {}),
        }}
      >
        <div
          className={styles.track}
          style={{
            transform: trackTransform,
            transition: animated
              ? "transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
              : "none",
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {looped.map((src, i) => {
            const isActive = i === index;
            return (
              <div
                key={i}
                className={`${styles.slide} ${isActive ? styles.active : ""}`}
                style={isMobile ? undefined : {
                  width: slideWidth > 0 ? slideWidth : `${slideRatio * 100}%`,
                  marginRight: GAP,
                }}
                onClick={() => {
                  if (!isActive) {
                    if (i < index) { prev(); startAutoplay(); }
                    else { next(); startAutoplay(); }
                  }
                }}
              >
                <img src={src} alt={alt} loading="lazy" draggable={false} onLoad={handleImgLoad} />
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.nav}>
        <button
          className={styles.navBtn}
          onClick={() => { prev(); startAutoplay(); }}
          aria-label="Previous"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
            <path stroke="#202428" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <div className={styles.pagination}>
          {images.map((_, i) => (
            <React.Fragment key={i}>
              <button
                className={`${styles.dot} ${i === realIndex ? styles.dotActive : ""}`}
                onClick={() => { goTo(i); startAutoplay(); }}
              >
                {i + 1}
              </button>
              {i < images.length - 1 && <div className={styles.dotLine} />}
            </React.Fragment>
          ))}
        </div>

        <button
          className={styles.navBtn}
          onClick={() => { next(); startAutoplay(); }}
          aria-label="Next"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
            <path stroke="#202428" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 6l6 6-6 6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default RepairSlider;
